import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useCategories } from "~/api/swr/useCategories";
import { type CategoryType } from "~/api/types";
import { updateCategoryOrder } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { SortableReorderList } from "~/molecules/SortableReorderList";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ReorderCategoryModal = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation();
  const { data: categories, mutate: mutateAll } = useCategories();
  const [categoriesState, setCategoriesState] = useState<CategoryType[] | null>(
    null,
  );
  const reorderedCategories =
    categoriesState ?? categories?.toSorted((a, b) => a.order - b.order) ?? [];

  const handleSave = async () => {
    try {
      await updateCategoryOrder(
        reorderedCategories.map((category, index) => ({
          ...category,
          order: index,
        })),
      );
      void mutateAll();
      toast.success(
        `${t(($) => $.commons.document.categories)} ${t(($) => $.toasts.success.reorder)}`,
      );
      setCategoriesState(null);
      onOpenChange(false);
    } catch {
      toast.error(t(($) => $.toasts.error.reorder));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) setCategoriesState(null);
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {t(($) => $.commons.buttons.reorderCategories)}
          </DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          <SortableReorderList
            items={reorderedCategories}
            onChange={setCategoriesState}
            sortableType="CATEGORY_ROW"
          />
        </div>
        <DialogFooter className="shrink-0">
          <Button disabled={!categories} onClick={handleSave}>
            {t(($) => $.commons.buttons.save)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
